import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { siteConfig, faqData, submitContactForm } from '../data/mockData';
import { toast } from 'sonner';

const Contatti = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success('Richiesta inviata!', {
          description: 'Ti contatteremo entro 24 ore per fissare la tua prova gratuita.',
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      toast.error('Errore', {
        description: 'Si è verificato un errore. Riprova o contattaci direttamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/31849599/pexels-photo-31849599.jpeg"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        </div>

        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Inizia <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Oggi</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              La tua trasformazione inizia con una conversazione. Prenota la tua prova gratuita.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div>
              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-3xl font-black text-white">
                    Prenota la Tua <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Prova Gratuita</span>
                  </CardTitle>
                  <p className="text-gray-400 mt-2">
                    Compila il form e ti ricontatteremo entro 24 ore per fissare un appuntamento.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-white font-semibold mb-2 block">
                        Nome Completo *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                        placeholder="Il tuo nome"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white font-semibold mb-2 block">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                        placeholder="tua@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-white font-semibold mb-2 block">
                        Telefono *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                        placeholder="+39 333 123 4567"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-white font-semibold mb-2 block">
                        Messaggio
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500 resize-none"
                        placeholder="Raccontaci i tuoi obiettivi (facoltativo)"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-6 text-base uppercase tracking-wide disabled:opacity-50"
                    >
                      {isSubmitting ? 'Invio in corso...' : 'Prenota Prova Gratuita'}
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Ti ricontatteremo entro 24 ore per confermare l'appuntamento
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              {/* Contact Methods */}
              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-black text-white">
                    Contattaci Direttamente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1">Telefono / WhatsApp</h3>
                      <a href={`tel:${siteConfig.contact.phone}`} className="text-orange-500 hover:text-orange-400 transition-colors">
                        {siteConfig.contact.phone}
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Lun-Ven: 7:00 - 21:00</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1">Email</h3>
                      <a href={`mailto:${siteConfig.contact.email}`} className="text-orange-500 hover:text-orange-400 transition-colors">
                        {siteConfig.contact.email}
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Rispondiamo entro 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1">Indirizzo</h3>
                      <p className="text-gray-300">{siteConfig.contact.address}</p>
                      <p className="text-sm text-gray-500 mt-1">Solo su appuntamento</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1">Orari</h3>
                      <div className="text-gray-300 space-y-1">
                        <p>Lun-Ven: 7:00 - 21:00</p>
                        <p>Sabato: 8:00 - 14:00</p>
                        <p>Domenica: Chiuso</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Why Book */}
              <Card className="bg-gradient-to-br from-orange-500/10 to-green-400/10 border border-orange-500/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-black text-white">
                    Cosa Include la Prova Gratuita
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">Valutazione fisica completa</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">Analisi obiettivi e aspettative</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">Sessione di allenamento guidata</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">Piano personalizzato preliminare</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">Zero impegno, zero costi nascosti</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 text-center">
              Domande <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Frequenti</span>
            </h2>
            <p className="text-gray-400 text-center mb-12">
              Risposte alle domande più comuni. Non trovi quello che cerchi? Contattaci.
            </p>

            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="bg-white/5 border border-white/10 rounded-lg px-6 backdrop-blur-sm hover:border-orange-500/50 transition-all"
                >
                  <AccordionTrigger className="text-white font-bold text-left hover:text-orange-500 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-zinc-900 relative overflow-hidden">
        <iframe
          src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(siteConfig.contact.addressForMaps)}&zoom=15`}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Performove Training Lab Location"
          className="w-full h-full"
        ></iframe>
        <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md px-4 py-3 rounded-lg border border-white/10">
          <div className="flex items-center space-x-2 text-white">
            <MapPin className="w-5 h-5 text-orange-500" />
            <div>
              <p className="text-sm font-bold">Performove Training Lab</p>
              <p className="text-xs text-gray-400">{siteConfig.contact.address}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
